'use client'

import { useRef } from 'react'
import Link from 'next/link'
import { motion, useInView } from 'framer-motion'
import { Code2, Layers, Globe, Palette, TrendingUp, ArrowRight } from 'lucide-react'

const services = [
  {
    number: '01',
    title: 'Web Development',
    description: 'Performant, pixel-perfect websites built with modern frameworks and cutting-edge tools.',
    icon: Globe,
    href: '/services/it#web-dev',
  },
  {
    number: '02',
    title: 'Full Stack Development',
    description: 'End-to-end product engineering — from scalable APIs to responsive frontends.',
    icon: Layers,
    href: '/services/it#fullstack',
  },
  {
    number: '03',
    title: 'WordPress Sites',
    description: 'Fast, secure, SEO-ready CMS solutions tailored to your content strategy.',
    icon: Code2,
    href: '/services/it#wordpress',
  },
  {
    number: '04',
    title: 'UI/UX Design',
    description: 'Research-led design systems and Figma prototypes that convert.',
    icon: Palette,
    href: '/services/it#uiux',
  },
  {
    number: '05',
    title: 'SEO',
    description: 'Technical SEO, Core Web Vitals, and content strategy that dominates search rankings.',
    icon: TrendingUp,
    href: '/services/it#seo',
  },
]

const otherServices = [
  { label: 'Videography', href: '/services/videography' },
  { label: 'Graphic Design', href: '/services/graphic-design' },
  { label: 'More Services', href: '/services/it' },
]

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const cardRef = useRef(null)
  const Icon = service.icon

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col justify-between border border-white/10 bg-[#1E293B] p-8 min-h-[380px] cursor-pointer overflow-hidden hover:border-[#C8A96E]/30 transition-all duration-500"
      style={{
        perspective: '1000px',
      }}
    >
      {/* Holographic shimmer bg */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(200,169,110,0.04) 0%, transparent 70%)',
        }}
      />

      {/* Gold accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#C8A96E] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-bottom" />

      {/* Number */}
      <span
        className="text-[#C8A96E]/30 text-5xl leading-none group-hover:text-[#C8A96E]/60 transition-colors duration-300"
        style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
      >
        {service.number}
      </span>

      {/* Icon + Content */}
      <div>
        <Icon
          size={36}
          className="text-[#94A3B8] group-hover:text-[#C8A96E] transition-colors duration-300 mb-5"
          strokeWidth={1.5}
        />
        <h3
          className="text-2xl md:text-3xl text-[#F8FAFC] mb-3 transition-colors duration-300"
          style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.05em' }}
        >
          {service.title}
        </h3>
        <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
          {service.description}
        </p>
        <Link
          href={service.href}
          className="inline-flex items-center gap-2 text-[#C8A96E] text-xs tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
        >
          LEARN MORE <span className="sr-only">about {service.title}</span> <ArrowRight size={12} />
        </Link>
      </div>
    </motion.div>
  )
}

export default function ServicesSection() {
  return (
    <section className="relative py-32 bg-[#0F172A]">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-px bg-[#C8A96E]" />
            <span
              className="text-[#C8A96E] text-xs tracking-[0.3em]"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              WHAT WE DO
            </span>
          </div>
          <h2
            className="text-[clamp(3rem,8vw,7rem)] leading-none text-[#F8FAFC]"
            style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
          >
            IT SERVICES
          </h2>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px">
          {services.map((service, index) => (
            <ServiceCard key={service.number} service={service} index={index} />
          ))}
        </div>

        {/* Other service links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 flex flex-wrap gap-8 border-t border-white/10 pt-8"
        >
          {otherServices.map((s) => (
            <Link
              key={s.label}
              href={s.href}
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#C8A96E] transition-colors text-sm tracking-[0.1em]"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
            >
              {s.label} <ArrowRight size={13} />
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
